import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebaseConfig";

const NAFED_LOGO = "/images/nafed-logo.png";

const upper = (value) => String(value || "").trim().toUpperCase();
const text = (value, fallback = "—") =>
  String(value || "").trim() || fallback;
const list = (value) =>
  Array.isArray(value)
    ? value.filter(Boolean)
    : String(value || "")
        .split(/[,;|]/)
        .map((item) => item.trim())
        .filter(Boolean);
const stateKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");

const contactStates = (contact) => {
  const values = [...list(contact.states), contact.state]
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  return [...new Map(values.map((item) => [stateKey(item), item])).values()];
};

const phoneHref = (value) => {
  const cleaned = String(value || "").replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : "";
};

function ContactValue({ icon, label, value, href }) {
  const shown = text(value);
  return (
    <div className="nc-value">
      <span className="nc-value-icon" aria-hidden="true">{icon}</span>
      <div>
        <small>{label}</small>
        {href && shown !== "—" ? <a href={href}>{shown}</a> : <b>{shown}</b>}
      </div>
    </div>
  );
}

function BranchCard({ branch }) {
  const phone = branch.officialMobile || branch.alternatePhone;
  return (
    <article className="nc-branch-card">
      <div className="nc-branch-title">
        <span className="nc-seal" aria-hidden="true">🏛</span>
        <div>
          <small>APPROVED NAFED STATE BRANCH</small>
          <h3>{text(branch.organizationName, `${branch.state} State Branch`)}</h3>
          <span className="nc-approved">● APPROVED &amp; ACTIVE</span>
        </div>
      </div>
      <div className="nc-branch-info">
        <ContactValue icon="👤" label="Nodal / Contact Officer" value={branch.contactPerson} />
        <ContactValue icon="☎" label="Official Contact" value={phone} href={phoneHref(phone)} />
        <ContactValue icon="✉" label="Official Email" value={branch.officialEmail} href={branch.officialEmail ? `mailto:${branch.officialEmail}` : ""} />
        <ContactValue icon="⌖" label="Branch Office Address" value={branch.fullAddress} />
      </div>
    </article>
  );
}

function PmcTable({ rows }) {
  if (!rows.length) {
    return (
      <div className="nc-empty-small">
        Is state ke liye koi approved active PMC public directory me available nahi hai.
      </div>
    );
  }
  return (
    <div className="nc-table-scroll">
      <table className="nc-table">
        <thead>
          <tr>
            <th>Approved PMC Agency</th>
            <th>Contact Person</th>
            <th>Official Contact</th>
            <th>Email</th>
            <th>Office Address</th>
            <th>District Coverage</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((pmc) => {
            const phone = pmc.officialMobile || pmc.alternatePhone;
            return (
              <tr key={pmc.id}>
                <td>
                  <b className="nc-pmc-name">{text(pmc.organizationName)}</b>
                  <span className="nc-status-chip">APPROVED</span>
                </td>
                <td>
                  <b>{text(pmc.contactPerson)}</b>
                  <small>{text(pmc.designation, "")}</small>
                </td>
                <td>{phone ? <a href={phoneHref(phone)}>{phone}</a> : "—"}</td>
                <td>{pmc.officialEmail ? <a href={`mailto:${pmc.officialEmail}`}>{pmc.officialEmail}</a> : "—"}</td>
                <td className="nc-address">{text(pmc.fullAddress)}</td>
                <td>{list(pmc.districts).length ? list(pmc.districts).join(", ") : "State-wide / As allotted"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function NafedContactPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [queryText, setQueryText] = useState("");
  const [stateFilter, setStateFilter] = useState("ALL");

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "nafed_public_contacts"),
      (snapshot) => {
        setContacts(
          snapshot.docs
            .map((document) => ({ id: document.id, ...document.data() }))
            .filter(
              (item) =>
                item.isDeleted !== true &&
                item.visibility === "PUBLIC" &&
                ["STATE_BRANCH", "PMC", "PMC_AGENCY"].includes(upper(item.role)),
            ),
        );
        setLoading(false);
        setError("");
      },
      (snapshotError) => {
        console.error("Public contacts listener failed:", snapshotError);
        setLoading(false);
        setError(
          snapshotError?.code === "permission-denied"
            ? "Public contact directory permission is not deployed yet."
            : "Live contact directory could not be loaded.",
        );
      },
    );
    return unsubscribe;
  }, []);

  const branches = useMemo(
    () =>
      contacts
        .filter((item) => upper(item.role) === "STATE_BRANCH")
        .sort((a, b) =>
          `${a.state} ${a.organizationName}`.localeCompare(
            `${b.state} ${b.organizationName}`,
          ),
        ),
    [contacts],
  );

  const pmcs = useMemo(
    () =>
      contacts
        .filter((item) => ["PMC", "PMC_AGENCY"].includes(upper(item.role)))
        .sort((a, b) =>
          String(a.organizationName || "").localeCompare(
            String(b.organizationName || ""),
          ),
        ),
    [contacts],
  );

  const states = useMemo(() => {
    const values = new Map();
    branches.forEach((item) => {
      if (item.state) values.set(stateKey(item.state), item.state);
    });
    pmcs.forEach((item) => {
      contactStates(item).forEach((state) =>
        values.set(stateKey(state), state),
      );
    });
    return [...values.values()].sort((a, b) => a.localeCompare(b));
  }, [branches, pmcs]);

  // Directory open hote hi pehla live state select ho. User dropdown se
  // kisi bhi doosre state/UT ya "All States" ko baad me select kar sakta hai.
  useEffect(() => {
    setStateFilter((current) =>
      current === "ALL" && states.length ? stateKey(states[0]) : current,
    );
  }, [states]);

  const stateSections = useMemo(() => {
    const search = queryText.trim().toLowerCase();
    return states
      .filter(
        (state) =>
          stateFilter === "ALL" || stateKey(state) === stateFilter,
      )
      .map((state) => {
        const key = stateKey(state);
        const stateBranches = branches.filter(
          (item) => stateKey(item.state) === key,
        );
        const statePmcs = pmcs.filter((item) =>
          contactStates(item).some((value) => stateKey(value) === key),
        );
        const haystack = [...stateBranches, ...statePmcs]
          .map((item) =>
            [
              item.organizationName,
              item.contactPerson,
              item.officialEmail,
              item.officialMobile,
              item.fullAddress,
              list(item.districts).join(" "),
            ]
              .join(" ")
              .toLowerCase(),
          )
          .join(" ");
        return { state, branches: stateBranches, pmcs: statePmcs, haystack };
      })
      .filter(
        (section) =>
          !search ||
          section.state.toLowerCase().includes(search) ||
          section.haystack.includes(search),
      );
  }, [states, stateFilter, queryText, branches, pmcs]);

  return (
    <div className="nafed-contact">
      <style>{`
        *{box-sizing:border-box}.nafed-contact{min-height:100vh;background:#f2f6fa;color:#071e3e;font-family:Inter,"Segoe UI",Arial,sans-serif}.nc-top{height:30px;padding:0 30px;background:#061b3b;color:#fff;display:flex;align-items:center;justify-content:space-between;font-size:11px}.nc-live{display:inline-flex;align-items:center;gap:8px}.nc-live:before{content:"";width:8px;height:8px;border-radius:50%;background:#55df6d;box-shadow:0 0 0 4px rgba(85,223,109,.14)}.nc-header{height:106px;padding:0 34px;background:#fff;border-bottom:1px solid #c8d5e2;display:flex;align-items:center;justify-content:space-between}.nc-brand{display:flex;align-items:center;gap:20px}.nc-logo{width:95px;height:80px;object-fit:contain;padding-right:18px;border-right:1px solid #cad5df}.nc-brand h1{margin:0;font-size:31px;letter-spacing:-.04em}.nc-brand h1 span{font-weight:450}.nc-brand p{margin:6px 0 0;color:#b45b0b;font-weight:700}.nc-actions{display:flex;gap:10px}.nc-button{display:inline-flex;height:42px;padding:0 20px;align-items:center;justify-content:center;border:1px solid #0a6737;border-radius:5px;background:#fff;color:#07562f;text-decoration:none;font-weight:800}.nc-button.primary{background:#075e30;color:#fff}.nc-nav{height:50px;background:#fff;border-bottom:1px solid #bdcddd;display:flex;align-items:center;justify-content:center;gap:36px}.nc-nav a{height:100%;display:flex;align-items:center;gap:7px;color:#122b48;text-decoration:none;font-weight:800;padding:0 15px;border-bottom:3px solid transparent}.nc-nav a:hover,.nc-nav a.active{color:#08723d;border-bottom-color:#08723d;background:#f4faf6}.nc-hero{background:linear-gradient(120deg,#062a57,#075c38);color:#fff;padding:30px 38px 27px;border-bottom:3px solid #e9a522}.nc-hero small{font-weight:900;letter-spacing:.14em;color:#81ef98}.nc-hero h2{font-size:34px;margin:7px 0}.nc-hero p{max-width:760px;color:#d9e8e3;margin:0;line-height:1.55}.nc-main{padding:20px 28px 38px;max-width:1600px;margin:auto}.nc-summary{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:-38px;margin-bottom:18px}.nc-summary-card{background:#fff;border:1px solid #b7c8d7;border-top:4px solid #16814a;border-radius:9px;padding:16px 18px;box-shadow:0 8px 25px rgba(3,34,67,.12)}.nc-summary-card span{font-size:11px;color:#536a7c;font-weight:800}.nc-summary-card b{display:block;font-size:26px;margin-top:4px}.nc-tools{display:grid;grid-template-columns:1fr 300px;gap:10px;background:#fff;border:1px solid #9fb1c1;border-radius:9px;padding:12px;margin-bottom:16px}.nc-tools input,.nc-tools select{width:100%;height:46px;border:1px solid #7f93a7;border-radius:6px;padding:0 14px;font:inherit;color:#102845;background:#fff}.nc-state-picker{display:grid;grid-template-columns:auto 1fr;align-items:center;gap:10px}.nc-state-picker label{font-size:11px;font-weight:900;color:#0b5f37;white-space:nowrap}.nc-section{background:#fff;border:1px solid #60758a;border-radius:10px;overflow:hidden;margin-bottom:16px;box-shadow:0 8px 24px rgba(8,38,70,.09)}.nc-section-head{background:linear-gradient(90deg,#092d59,#0b7040);color:#fff;padding:14px 17px;display:flex;align-items:center;justify-content:space-between}.nc-section-head h2{font-size:18px;margin:0}.nc-section-head h2 small{display:block;margin-top:3px;color:#d8ece2;font-size:9px;letter-spacing:.08em}.nc-section-head span{font-size:11px;border:1px solid rgba(255,255,255,.45);border-radius:20px;padding:5px 10px}.nc-subhead{margin:0;padding:11px 16px;background:#e9f1f7;border-bottom:1px solid #172638;color:#092b50;font-size:12px;font-weight:900;letter-spacing:.04em}.nc-branch-list{display:grid;grid-template-columns:repeat(auto-fit,minmax(390px,1fr));gap:12px;padding:13px;background:#f5f8fb;border-bottom:2px solid #172638}.nc-branch-card{border:1px solid #172638;border-radius:7px;background:#fff;overflow:hidden}.nc-branch-title{padding:14px 16px;display:flex;gap:13px;border-bottom:1px solid #172638}.nc-seal{width:45px;height:45px;border-radius:50%;display:grid;place-items:center;background:#e6f5eb;color:#08723d;font-size:21px}.nc-branch-title small{color:#0a7540;font-size:9px;font-weight:900;letter-spacing:.08em}.nc-branch-title h3{margin:4px 0;font-size:16px}.nc-approved{font-size:9px;color:#188249;font-weight:900}.nc-branch-info{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0}.nc-value{min-height:68px;padding:11px 13px;display:flex;gap:10px;border-right:1px solid #172638;border-bottom:1px solid #172638;min-width:0}.nc-value:nth-child(even){border-right:0}.nc-value:nth-last-child(-n+2){border-bottom:0}.nc-value-icon{color:#08713c;font-size:17px}.nc-value div{min-width:0}.nc-value small,.nc-value b,.nc-value a{display:block}.nc-value small{font-size:9px;color:#667b8d;margin-bottom:4px}.nc-value b,.nc-value a{font-size:12px;color:#0d2b4e;text-decoration:none;word-break:break-word}.nc-table-title{padding:12px 16px;font-size:13px;font-weight:900;margin:0;color:#fff;background:#172638;border-bottom:1px solid #05080c}.nc-table-scroll{overflow-x:auto;padding:0 12px 14px;background:#fff}.nc-table{width:100%;border-collapse:collapse;min-width:1000px;border:2px solid #111}.nc-table th{background:#162b40;color:#fff;text-align:left;font-size:10px;padding:11px;border:1px solid #111;letter-spacing:.03em}.nc-table td{padding:12px 10px;border:1px solid #111;font-size:11px;vertical-align:top;color:#142e47;background:#fff}.nc-table tbody tr:nth-child(even) td{background:#f1f4f6}.nc-table tr:hover td{background:#eaf7ef}.nc-table a{color:#075ca8;text-decoration:none;font-weight:700}.nc-table td small{display:block;color:#718493;margin-top:3px}.nc-pmc-name{display:block;max-width:310px;color:#082849}.nc-status-chip{display:inline-block;margin-top:5px;padding:3px 7px;border:1px solid #08743d;border-radius:10px;color:#08743d;background:#eff9f2;font-size:8px;font-weight:900}.nc-address{max-width:300px;line-height:1.45}.nc-empty,.nc-empty-small{padding:34px;text-align:center;color:#65798b}.nc-empty-small{padding:24px;border-top:1px solid #d7e1e9}.nc-error{background:#fff2f0;border:1px solid #e4b5ae;color:#9d2e20;padding:13px;border-radius:7px;margin-bottom:14px}.nc-bottom{height:5px;background:linear-gradient(90deg,#061d42,#0a743e)}
        @media(max-width:900px){.nc-header{height:auto;min-height:105px;padding:14px 18px;gap:14px}.nc-brand h1{font-size:22px}.nc-actions{flex-direction:column}.nc-nav{justify-content:flex-start;overflow:auto;gap:5px;padding:0 8px}.nc-nav a{white-space:nowrap}.nc-summary{grid-template-columns:repeat(2,1fr)}.nc-tools{grid-template-columns:1fr}.nc-branch-list{grid-template-columns:1fr}}@media(max-width:600px){.nc-top{padding:0 12px}.nc-top span:first-child{display:none}.nc-header{align-items:flex-start;flex-direction:column}.nc-logo{width:62px;height:58px}.nc-brand h1{font-size:18px}.nc-actions{width:100%;flex-direction:row}.nc-button{flex:1;padding:0 8px;font-size:11px}.nc-hero{padding:24px 18px 55px}.nc-hero h2{font-size:27px}.nc-main{padding:15px 10px 30px}.nc-summary{grid-template-columns:1fr 1fr;gap:7px}.nc-summary-card{padding:12px}.nc-summary-card b{font-size:20px}.nc-branch-list{padding:8px}.nc-branch-info{grid-template-columns:1fr}}
      `}</style>

      <div className="nc-top">
        <span>🇮🇳 Government of India</span>
        <span className="nc-live">Live Firestore Contact Directory</span>
      </div>
      <header className="nc-header">
        <div className="nc-brand">
          <img className="nc-logo" src={NAFED_LOGO} alt="NAFED" />
          <div>
            <h1>NAFED <span>Grain Storage Digital Mission</span></h1>
            <p>PM Anna Bhandaran Yojana · Official Contact Directory</p>
          </div>
        </div>
        <div className="nc-actions">
          <a className="nc-button primary" href="/nafed/login">🔒 &nbsp; Portal Login</a>
          <a className="nc-button" href="/nafed#register">♟ &nbsp; Registration</a>
        </div>
      </header>
      <nav className="nc-nav" aria-label="Public portal navigation">
        <a href="/nafed/guidelines">▧ Guidelines</a>
        <a href="/nafed#tenders">⚒ Tenders</a>
        <a href="/nafed#godown-registration">⌂ Godown Registration</a>
        <a className="active" href="/nafed/contact">☎ Contact</a>
      </nav>

      <section className="nc-hero">
        <small>VERIFIED PUBLIC DIRECTORY</small>
        <h2>State Branch &amp; Approved PMC Contacts</h2>
        <p>
          Approved NAFED State Branch ke official contact ke niche usi state
          ke approved active PMC agencies ki live contact directory.
        </p>
      </section>

      <main className="nc-main">
        <section className="nc-summary" aria-label="Contact directory summary">
          <div className="nc-summary-card"><span>Approved State Branches</span><b>{branches.length}</b></div>
          <div className="nc-summary-card"><span>Approved Active PMCs</span><b>{pmcs.length}</b></div>
          <div className="nc-summary-card"><span>Covered States</span><b>{states.length}</b></div>
          <div className="nc-summary-card"><span>Directory Status</span><b style={{color:"#148048",fontSize:18}}>● LIVE</b></div>
        </section>

        <section className="nc-tools" aria-label="Search contacts">
          <input
            type="search"
            value={queryText}
            onChange={(event) => setQueryText(event.target.value)}
            placeholder="Search branch, PMC, officer, district, mobile, email or address..."
            aria-label="Search public contacts"
          />
          <div className="nc-state-picker">
            <label htmlFor="nafed-contact-state">SELECT STATE / UT</label>
            <select
              id="nafed-contact-state"
              value={stateFilter}
              onChange={(event) => setStateFilter(event.target.value)}
              aria-label="Filter by state"
            >
              <option value="ALL">All States / UTs</option>
              {states.map((state) => (
                <option key={stateKey(state)} value={stateKey(state)}>{state}</option>
              ))}
            </select>
          </div>
        </section>

        {error && <div className="nc-error">{error}</div>}
        {loading ? (
          <div className="nc-empty">Live approved contacts load ho rahe hain…</div>
        ) : stateSections.length ? (
          stateSections.map((section) => (
            <section className="nc-section" key={stateKey(section.state)}>
              <div className="nc-section-head">
                <h2>
                  {section.state}
                  <small>LIVE VERIFIED STATE CONTACT DIRECTORY</small>
                </h2>
                <span>{section.branches.length} Branch · {section.pmcs.length} PMC</span>
              </div>
              <h3 className="nc-subhead">OFFICIAL NAFED STATE BRANCH DETAILS</h3>
              <div className="nc-branch-list">
                {section.branches.length ? (
                  section.branches.map((branch) => (
                    <BranchCard branch={branch} key={branch.id} />
                  ))
                ) : (
                  <div className="nc-empty-small">
                    Approved State Branch public contact record pending.
                  </div>
                )}
              </div>
              <h3 className="nc-table-title">
                APPROVED PMC AGENCIES — {upper(section.state)}
              </h3>
              <PmcTable rows={section.pmcs} />
            </section>
          ))
        ) : (
          <div className="nc-empty">
            Search/filter ke liye koi matching approved Branch ya PMC nahi mila.
          </div>
        )}
      </main>
      <div className="nc-bottom" />
    </div>
  );
}
